export function imageKey(image) {
  const key = image?.storageKey
  return key && !String(key).startsWith('blob:') ? String(key) : null
}

function clone(value) { return JSON.parse(JSON.stringify(value ?? null)) }

function mergeDecisionMap(current, draftIds) {
  const next = new Map(current || [])
  for (const id of draftIds || []) {
    if (!next.has(id)) next.set(id, { excelProductTypeDraftId: id, decision: 'CreateNew', existingProductTypeId: null })
  }
  return next
}

function hasCompleteDecisions(decisions, draftIds) {
  return (draftIds || []).every(id => {
    const decision = decisions?.get(id)
    return decision && (decision.decision === 'CreateNew' || (decision.decision === 'UseExisting' && Number(decision.existingProductTypeId) > 0))
  })
}

function buildInclusionRequest(review, draft, included) {
  return { reviewStateId: review.id, candidateKey: draft.candidateKey, expectedVersion: review.concurrencyVersion, included: Boolean(included) }
}

function selectedDraftCount(total, excludedCandidateKeys = []) {
  return Math.max(0, Number(total || 0) - new Set(excludedCandidateKeys || []).size)
}

function updateExcludedCandidateKeys(excludedCandidateKeys, candidateKey, included) {
  const excluded = new Set(excludedCandidateKeys || [])
  if (included) excluded.delete(candidateKey)
  else excluded.add(candidateKey)
  return [...excluded].sort()
}

function isCandidateIncluded(candidateKey, excludedCandidateKeys = []) {
  return !(excludedCandidateKeys || []).includes(candidateKey)
}

function buildOverrideRequest(review, draft) {
  return {
    reviewStateId: review.id,
    candidateKey: draft.candidateKey,
    excelProductTypeDraftId: draft.excelProductTypeDraftId,
    expectedVersion: review.concurrencyVersion,
    expectedOverrideVersion: draft.overrideVersion || 0,
    fields: {
      values: {
        sku: draft.values?.sku ?? null,
        basePrice: draft.values?.basePrice ?? null,
        salePrice: draft.values?.salePrice ?? null,
        saleIsActive: Boolean(draft.values?.saleIsActive),
        quantity: draft.values?.quantity ?? null
      },
      characteristics: (draft.characteristics || []).map(x => ({ productAttributeId: x.productAttributeId, characteristicValueId: x.characteristicValueId })),
      isDefault: draft.values?.isDefault ?? null
    }
  }
}

function buildImageSelections(images) {
  return (images || []).map((image, index) => {
    const storageKey = imageKey(image)
    return storageKey ? { storageKey, url: image.url || image.previewUrl || `/${storageKey}`, isDefault: Boolean(image.isDefault), sortOrder: image.sortOrder ?? index } : null
  }).filter(Boolean).sort((a, b) => a.sortOrder - b.sortOrder)
}

function mergeServerDraftImages(serverDraft, existingImages = []) {
  const serverImages = serverDraft?.images || []
  const images = serverImages.length ? serverImages : existingImages
  return images.map((image, index) => ({
    clientId: image.clientId || `server-${image.storageKey}-${image.sortOrder ?? index}`,
    storageKey: image.storageKey || null,
    previewUrl: image.url || image.previewUrl || (image.storageKey ? `/${image.storageKey}` : ''),
    url: image.url,
    isDefault: Boolean(image.isDefault),
    sortOrder: image.sortOrder ?? index,
    persisted: Boolean(image.storageKey && !String(image.storageKey).startsWith('blob:')),
    file: image.file
  }))
}

function changedDrafts(drafts, baseline) {
  const base = new Map((baseline || []).map(draft => [draft.id, draft]))
  return (drafts || []).filter(draft => {
    const original = base.get(draft.id)
    if (!original) return true
    return JSON.stringify({ values: draft.values, characteristics: draft.characteristics, images: buildImageSelections(draft.localImages) }) !==
      JSON.stringify({ values: original.values, characteristics: original.characteristics, images: buildImageSelections(original.localImages) })
  })
}

function canConfirm(preflight) { return Boolean(preflight && preflight.isValid === true) }
function canApply(snapshot, applyResult, applying) { return Boolean(snapshot?.payloadChecksum && !applying && applyResult?.status !== 'Applied') }

function classifyApplyError(error) {
  const data = error?.response?.data || {}
  const code = error?.code || error?.errorCode || data.code || data.errorCode || error?.metadata?.Code || data.metadata?.Code
  if (code === 'PRODUCT_DRAFT_APPLY_IN_PROGRESS') return 'in-progress'
  if (code === 'PRODUCT_DRAFT_SNAPSHOT_STALE' || code === 'PRODUCT_DRAFT_APPLY_CONCURRENCY_CONFLICT' || code === 'PRODUCT_DRAFT_SNAPSHOT_CHECKSUM_MISMATCH') return 'stale'
  if (code === 'PRODUCT_DRAFT_APPLY_FAILED') return 'failed'
  return 'other'
}

function normalizeApplyResult(result) {
  const products = (Array.isArray(result?.products) ? result.products : []).map(item => ({
    ...item,
    article: item.article || item.normalizedSku || '',
    outcome: item.outcome || 'Created'
  }))
  return {
    ...(result || {}),
    productTypes: result?.productTypes || [],
    products,
    createdProductCount: result?.createdProductCount ?? products.filter(item => !item.outcome || item.outcome === 'Created').length,
    skippedProductCount: result?.skippedProductCount ?? products.filter(item => item.outcome && item.outcome !== 'Created').length,
    status: result?.status || 'Unknown',
    attemptNumber: result?.attemptNumber || 0
  }
}

function resolveAppliedBrandId(applyResult, productTypes = []) {
  const ids = new Set((applyResult?.productTypes || [])
    .map(item => Number(item.productTypeId))
    .filter(Number.isInteger))
  const catalogTypes = Array.isArray(productTypes)
    ? productTypes
    : (productTypes?.items || productTypes?.data || [])
  const brandIds = new Set(catalogTypes
    .filter(item => ids.has(Number(item.id ?? item.Id)))
    .map(item => Number(item.brandId ?? item.BrandId))
    .filter(Number.isInteger))
  return brandIds.size === 1 ? [...brandIds][0] : null
}

async function saveDraftsSequentially(drafts, baseline, saveOne, initialVersion = 0) {
  let version = initialVersion
  const saved = []
  for (const draft of changedDrafts(drafts, baseline)) {
    const result = await saveOne(draft, version)
    saved.push({ draft, result })
    version = result.concurrencyVersion
  }
  return { saved, concurrencyVersion: version }
}







export { mergeDecisionMap, hasCompleteDecisions, buildInclusionRequest, selectedDraftCount, updateExcludedCandidateKeys, isCandidateIncluded, buildOverrideRequest, buildImageSelections, mergeServerDraftImages, changedDrafts, canConfirm, canApply, classifyApplyError, normalizeApplyResult, resolveAppliedBrandId, saveDraftsSequentially, clone }
